import React from 'react';
import { Button, Group, Dialog, DialogProps, useToast, Badge } from '@economic/taco';
import { t } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';

import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import track from '../../../../../shared/util/utilities/mixpanel';

interface Props extends Partial<DialogProps> {
    isManualSyncAllowed: boolean;
    refreshSettings: () => void;
    hasMoreAiiaIntegrations: boolean;
}

const badgeList = (text: string | JSX.Element): JSX.Element => {
    return (
        <div className="flex justify-start">
            <Badge aria-label="" className="mr-3">
                !
            </Badge>
            <div>{text}</div>
        </div>
    );
};

export const ManualSynchronisationDialog: React.FC<Props> = ({
    isManualSyncAllowed,
    refreshSettings,
    hasMoreAiiaIntegrations,
    ...dialogProps
}) => {
    const navigate = useNavigate();
    const toast = useToast();

    const handleManualSync = (hide: () => void) => {
        track('smartbank', 'ongoing-use.bank-connections.manual-sync-button');

        internalApiClient
            .post('smartbank/manualSynchronization')
            .then(response => {
                if (response.data === null || response.data === '') {
                    toast.success(t`All bank accounts have been synchronized.`);
                } else if (response.data.indexOf('http') > -1) {
                    window.open(encodeURI(response.data), '_blank');
                }
                refreshSettings();
            })
            .catch(() => {
                toast.error(t`Something went wrong, please try again later.`);
            })
            .finally(() => hide());
    };

    const handleClose = () => {
        // when opened from bank reconciliation
        if (window.location.href.indexOf('#') > -1) {
            navigate(location.pathname);
        }
    };

    return (
        <Dialog size="xs" closeOnEscape={false} onClose={handleClose} {...dialogProps}>
            <Dialog.Content aria-label="sync-modal">
                {({ close }) => (
                    <>
                        <h2>{t`Synchronise bank accounts`}</h2>
                        <div className="space-y-6 my-4">
                            {badgeList(
                                t`Synchronise bank accounts directly with the banks and retrieve transactions from the last week.`
                            )}
                            {badgeList(t`Due to legislation, synchronisation can only take place once a day.`)}
                            {!hasMoreAiiaIntegrations ? (
                                <>
                                    {badgeList(
                                        t`If the connection to the banks is inactive or requires a supervised login, you will be redirected to log in to the bank.`
                                    )}
                                </>
                            ) : (
                                <>
                                    {badgeList(
                                        t`Be aware that this will synchronize all the bank accounts of all agreements under this Aiia integration. If the connection to the bank is inactive or requires a supervised login, you will be redirected to log in the bank (regardless if the respective connection is on this agreement or another one that is connected under the same Aiia integration).`
                                    )}
                                </>
                            )}
                        </div>
                        <Dialog.Footer>
                            <div className="mt-10 flex flex-row-reverse">
                                <Group>
                                    <Dialog.Close>
                                        <Button appearance="default">{t`Cancel`}</Button>
                                    </Dialog.Close>
                                    <Button
                                        appearance="primary"
                                        type="submit"
                                        disabled={!isManualSyncAllowed}
                                        onClick={() =>
                                            handleManualSync(() => {
                                                close();
                                                handleClose();
                                            })
                                        }>
                                        {t`Sync`}
                                    </Button>
                                </Group>
                            </div>
                        </Dialog.Footer>
                    </>
                )}
            </Dialog.Content>
            <Dialog.Extra>{t`Be aware that it may take a few minutes before the transactions are completed.`}</Dialog.Extra>
        </Dialog>
    );
};
